import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import useTranslate from '../../../hooks/useTranslate';
import styles from './BlockControls.css';
import Button from '../../atoms/Button/Button';
import GridContext from '../Grid/GridContext';
import { bem } from '../../../helpers/styles';
import { getBlockDarkerColor } from '../../../helpers/color';
import ConfidenceIcon from '../../atoms/ConfidenceIcon/ConfidenceIcon';
import Dropdown from '../../molecules/Dropdown/Dropdown';
import { useDispatch } from 'react-redux';
import { arrayPush } from 'redux-form';
import FieldError from '../../molecules/Error/FieldError';

const BlockControls = ({
  block,
  folded,
  onFoldClick,
  onDeleteAllClick,
  readonly,
  fieldName,
  reviewOptions,
  totalDuplicates,
  error,
}) => {
  //BLOCKS HEADER
  const i18n = useTranslate();
  const dispatch = useDispatch();

  const grid = useContext(GridContext);

  const style = useMemo(
    () => ({
      height: grid?.rowHeight || undefined,
    }),
    [grid?.rowHeight],
  );

  const darkerColor02 = useMemo(() => getBlockDarkerColor(block.color, 0.2), [block.color]);
  const darkerColor = useMemo(() => getBlockDarkerColor(block.color), [block.color]);
  const colorStyle = useMemo(
    () => ({
      color: darkerColor,
      fill: darkerColor,
      '--btn-hover-bg': darkerColor02,
    }),
    [darkerColor, darkerColor02],
  );

  const handleReviewRowClick = (value) => {
    dispatch(arrayPush('contribute', fieldName, value));
  };

  const checkAnswerValue =
    reviewOptions &&
    reviewOptions.rows.some((row) => row.label !== null);

  return (
    <div className={styles.BlockControls} style={style}>
      {block.foldable && (
        <Button
          type="button"
          icon="caret"
          rounded
          flat
          tabIndex={-1}
          className={bem(styles, 'BlockControls__buttonFold', { folded, noHandler: !onFoldClick })}
          style={colorStyle}
          onClick={onFoldClick}
        />
      )}
      {block.nameEnabled && (
        <div className={styles.BlockControls__name} style={colorStyle}>
          {block.name}
        </div>
      )}
      {folded && block.duplicable && (
        <div
          className={styles.BlockControls__increment}
          style={{ color: darkerColor, border: `1px solid ${darkerColor02}` }}
        >
          {totalDuplicates}
        </div>
      )}
      {reviewOptions && reviewOptions.nbFields > 0 && checkAnswerValue && (
        <div className={styles.BlockControls__review}>
          <ConfidenceIcon confidence={reviewOptions.confidence} />
          <Dropdown
            options={reviewOptions.rows}
            icon="arrow_dropdown_down"
            onSelect={handleReviewRowClick}
          />
        </div>
      )}
      <div className={styles.BlockControls__flex}>{error && <FieldError error={error} />}</div>
      {block.duplicable && (
        <Button
          type="button"
          icon="trash_outlined"
          flat
          tabIndex={-1}
          className={bem(styles, 'BlockControls__emptyButton', { noHandler: !onDeleteAllClick })}
          style={colorStyle}
          onClick={onDeleteAllClick}
          disabled={readonly}
        >
          {i18n('ContributeFormTemplate.clearBlock')}
        </Button>
      )}
    </div>
  );
};

BlockControls.propTypes = {
  block: PropTypes.object,
  onFoldClick: PropTypes.func,
  folded: PropTypes.bool,
  readonly: PropTypes.bool,
  fieldName: PropTypes.string,
  reviewOptions: PropTypes.shape({
    confidence: PropTypes.number,
    rows: PropTypes.array,
    nbFields: PropTypes.number,
  }),
  error: PropTypes.object,
  totalDuplicates: PropTypes.number,
  onDeleteAllClick: PropTypes.func,
};

export default BlockControls;
